import { conversationIdFromNextStep } from "@/lib/entityDeepLinks";
import { buildConversationTree, type ConversationTreeNode } from "@/lib/buildConversationTree";
import { ACTION_TYPE_LABEL, STAGE_LABEL } from "@/lib/workflowStageLabels";
import { formatHubChipLabel } from "@/lib/hubChipLabels";
import type {
  CNSConversation,
  CNSWorkflowStage,
  CNSProjectNextStep,
  CNSProjectNextStepSeverity,
} from "@/models/cns";

export const PATH_SEVERITY_ORDER: CNSProjectNextStepSeverity[] = [
  "OVERDUE",
  "ATTENTION",
  "INFO",
];

export type ProjectHomeDecisionRow = {
  mode: "decision";
  step: CNSProjectNextStep;
  conversation: CNSConversation | null;
};

export type ProjectHomePassiveRow = {
  mode: "passive";
  conversation: CNSConversation;
  depth: number;
  childCount: number;
};

export type ProjectHomePathRow = ProjectHomeDecisionRow | ProjectHomePassiveRow;

/**
 * Conversation ids already represented by a next-step decision row.
 */
export function hitlConversationIdsFromSteps(
  steps: CNSProjectNextStep[] | null | undefined,
): Set<string> {
  const ids = new Set<string>();
  for (const item of steps ?? []) {
    const id = conversationIdFromNextStep(item);
    if (id) ids.add(id);
  }
  return ids;
}

/**
 * Builds a conversation lookup for joining why-lines onto decision rows.
 */
export function conversationByIdMap(
  conversations: CNSConversation[],
): Map<string, CNSConversation> {
  return new Map(conversations.map((c) => [c.id, c]));
}

/**
 * Sorts decision steps by severity tier (OVERDUE → ATTENTION → INFO), then newest
 * first within each tier (lower ageDays), then stepId for stability.
 */
export function sortDecisionSteps(steps: CNSProjectNextStep[]): CNSProjectNextStep[] {
  const rank = new Map(PATH_SEVERITY_ORDER.map((s, i) => [s, i]));
  return [...steps].sort((a, b) => {
    const severityDiff = (rank.get(a.severity) ?? 99) - (rank.get(b.severity) ?? 99);
    if (severityDiff !== 0) return severityDiff;
    const ageDiff = a.ageDays - b.ageDays;
    if (ageDiff !== 0) return ageDiff;
    return a.stepId.localeCompare(b.stepId);
  });
}

/**
 * Passive path tree for conversations not already covered by decision rows.
 */
export function buildPassivePathTree(
  conversations: CNSConversation[],
  hitlIds: Set<string>,
): ConversationTreeNode[] {
  const filtered = conversations.filter((c) => !hitlIds.has(c.id));
  return buildConversationTree(filtered);
}

/**
 * Flattens a conversation tree into passive rows with depth and descendant counts.
 */
export function flattenPassiveTree(
  tree: ConversationTreeNode[],
  countDescendants: (node: ConversationTreeNode) => number,
): ProjectHomePassiveRow[] {
  const rows: ProjectHomePassiveRow[] = [];
  const walk = (node: ConversationTreeNode, depth: number) => {
    rows.push({
      mode: "passive",
      conversation: node.conversation,
      depth,
      childCount: countDescendants(node),
    });
    for (const child of node.children) {
      walk(child, depth + 1);
    }
  };
  for (const root of tree) {
    walk(root, 0);
  }
  return rows;
}

/**
 * Groups sorted decision steps by severity for overdue labeling.
 */
export function groupDecisionsBySeverity(
  steps: CNSProjectNextStep[],
): { severity: CNSProjectNextStepSeverity; steps: CNSProjectNextStep[] }[] {
  const groups: Partial<Record<CNSProjectNextStepSeverity, CNSProjectNextStep[]>> = {};
  for (const step of steps) {
    const bucket = groups[step.severity] ?? [];
    bucket.push(step);
    groups[step.severity] = bucket;
  }
  return PATH_SEVERITY_ORDER.filter((severity) => (groups[severity]?.length ?? 0) > 0).map(
    (severity) => ({ severity, steps: groups[severity]! }),
  );
}

export function duplicateEntityTitles(steps: CNSProjectNextStep[]): Set<string> {
  const counts = new Map<string, number>();
  for (const step of steps) {
    const title = step.entityTitle.trim();
    counts.set(title, (counts.get(title) ?? 0) + 1);
  }
  return new Set(
    [...counts.entries()].filter(([, count]) => count > 1).map(([title]) => title),
  );
}

function stepCreatedSortKey(
  step: CNSProjectNextStep,
  conversationById: Map<string, CNSConversation>,
): number {
  const conv = step.conversationPublicId
    ? conversationById.get(step.conversationPublicId)
    : undefined;
  if (conv?.createdAt) return Date.parse(conv.createdAt);
  return -step.ageDays;
}

/**
 * Assigns conversation index 1..N within each duplicate-title group (oldest conversation first).
 */
export function decisionPathIndexesForDuplicates(
  steps: CNSProjectNextStep[],
  conversationById: Map<string, CNSConversation>,
): Map<string, number> {
  const byTitle = new Map<string, CNSProjectNextStep[]>();
  for (const step of steps) {
    const title = step.entityTitle.trim();
    const bucket = byTitle.get(title) ?? [];
    bucket.push(step);
    byTitle.set(title, bucket);
  }

  const indexes = new Map<string, number>();
  for (const group of byTitle.values()) {
    if (group.length <= 1) continue;
    const sorted = [...group].sort((a, b) => {
      const timeDiff =
        stepCreatedSortKey(a, conversationById) - stepCreatedSortKey(b, conversationById);
      if (timeDiff !== 0) return timeDiff;
      return a.stepId.localeCompare(b.stepId);
    });
    sorted.forEach((step, index) => indexes.set(step.stepId, index + 1));
  }
  return indexes;
}

function formatShortRelativeTime(dateString: string, now = new Date()): string {
  const date = new Date(dateString);
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInHours / 24;

  if (diffInHours < 1) return "just now";
  if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }
  if (diffInDays < 2) return "yesterday";
  if (diffInDays < 7) {
    return date.toLocaleDateString("en-US", { weekday: "short" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatStartedLabel(
  conversation: CNSConversation | null,
  ageDays: number,
  now = new Date(),
): string {
  if (conversation?.createdAt) {
    return `started ${formatShortRelativeTime(conversation.createdAt, now)}`;
  }
  if (ageDays <= 0) return "started today";
  if (ageDays === 1) return "started yesterday";
  if (ageDays < 7) return `started ${ageDays}d ago`;
  if (ageDays < 30) return `started ${Math.floor(ageDays / 7)}w ago`;
  if (ageDays < 365) return `started ${Math.floor(ageDays / 30)}mo ago`;
  return `started ${Math.floor(ageDays / 365)}y ago`;
}

function decisionActionStartedLabel(
  step: CNSProjectNextStep,
  conversation: CNSConversation | null,
  now: Date,
): string {
  const started = formatStartedLabel(conversation, step.ageDays, now);
  const action = step.primaryActionLabel.trim();
  return action ? `${action} · ${started}` : started;
}

/**
 * Subtitle for decision rows whose titles collide in the visible list.
 * Prefers action + started time; falls back to conversation index when that still collides.
 */
export function formatDecisionRowDisambiguator(
  step: CNSProjectNextStep,
  conversation: CNSConversation | null,
  options: {
    duplicateTitles: Set<string>;
    pathIndexes: Map<string, number>;
    decisionSteps: CNSProjectNextStep[];
    conversationById: Map<string, CNSConversation>;
    now?: Date;
  },
): string | null {
  const title = step.entityTitle.trim();
  if (!options.duplicateTitles.has(title)) return null;

  const now = options.now ?? new Date();
  const peers = options.decisionSteps.filter((peer) => peer.entityTitle.trim() === title);
  const actionStarted = decisionActionStartedLabel(step, conversation, now);
  const matchingActionStarted = peers.filter((peer) => {
    const peerConversation = peer.conversationPublicId
      ? (options.conversationById.get(peer.conversationPublicId) ?? null)
      : null;
    return decisionActionStartedLabel(peer, peerConversation, now) === actionStarted;
  });

  if (matchingActionStarted.length === 1) {
    return actionStarted;
  }

  const started = formatStartedLabel(conversation, step.ageDays, now);
  const conversationIndex = options.pathIndexes.get(step.stepId);
  if (conversationIndex != null) {
    return `${started} · conversation ${conversationIndex}`;
  }
  return started;
}

export function normalizeConversationSearchQuery(query: string): string {
  return query.trim().toLowerCase();
}

export function conversationSearchHaystackForDecision(
  step: CNSProjectNextStep,
  conversation: CNSConversation | null,
): string {
  const parts = [
    step.entityTitle,
    step.primaryActionLabel,
    step.kind.replaceAll("_", " "),
    step.severity,
    step.entityType,
    conversation?.title,
    step.actionType ? ACTION_TYPE_LABEL[step.actionType] ?? step.actionType : null,
    step.assigneeUserPublicId ? "assigned" : "unassigned",
  ];
  return parts.filter(Boolean).join(" ").toLowerCase();
}

export function conversationSearchHaystackForPassive(conversation: CNSConversation): string {
  const parts = [
    conversation.title,
    conversation.lastAgentActionLine,
    conversation.attentionHint,
    conversation.currentStage && conversation.currentStage in STAGE_LABEL
      ? STAGE_LABEL[conversation.currentStage as CNSWorkflowStage]
      : null,
    conversation.primaryArtifactLabel,
    formatHubChipLabel(conversation.primaryArtifactStatus),
    conversation.parentConversationPublicId ? "branched" : null,
  ];
  return parts.filter(Boolean).join(" ").toLowerCase();
}

function matchesConversationSearch(haystack: string, normalizedQuery: string): boolean {
  return haystack.includes(normalizedQuery);
}

export function filterDecisionSteps(
  steps: CNSProjectNextStep[],
  query: string,
  conversationsById: Map<string, CNSConversation>,
): CNSProjectNextStep[] {
  const normalizedQuery = normalizeConversationSearchQuery(query);
  if (!normalizedQuery) return steps;
  return steps.filter((step) => {
    const convId = step.conversationPublicId;
    const conversation = convId ? (conversationsById.get(convId) ?? null) : null;
    return matchesConversationSearch(
      conversationSearchHaystackForDecision(step, conversation),
      normalizedQuery,
    );
  });
}

export function filterPassiveRows(
  rows: ProjectHomePassiveRow[],
  query: string,
): ProjectHomePassiveRow[] {
  const normalizedQuery = normalizeConversationSearchQuery(query);
  if (!normalizedQuery) return rows;
  const matchingIds = new Set(
    rows
      .filter((row) =>
        matchesConversationSearch(
          conversationSearchHaystackForPassive(row.conversation),
          normalizedQuery,
        ),
      )
      .map((row) => row.conversation.id),
  );
  if (matchingIds.size === 0) return [];

  const includedIds = new Set<string>();
  for (const row of rows) {
    if (!matchingIds.has(row.conversation.id)) continue;
    let current: ProjectHomePassiveRow | undefined = row;
    while (current) {
      includedIds.add(current.conversation.id);
      const parentId: string | null | undefined =
        current.conversation.parentConversationPublicId;
      if (!parentId) break;
      current = rows.find((candidate) => candidate.conversation.id === parentId);
    }
  }
  return rows.filter((row) => includedIds.has(row.conversation.id));
}

/** Loaded conversation count at which we show a pagination coverage hint on project home. */
export const PROJECT_HOME_CONVERSATIONS_LOADED_HINT_THRESHOLD = 20;

function passiveRowRecencyMs(row: ProjectHomePassiveRow): number {
  const source = row.conversation.updatedAt ?? row.conversation.createdAt;
  const ms = source ? Date.parse(source) : 0;
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Returns the most recently updated passive rows up to limit.
 */
export function pickRecentPassiveRows(
  passiveRows: ProjectHomePassiveRow[],
  limit = 4,
): ProjectHomePassiveRow[] {
  if (passiveRows.length <= limit) return passiveRows;
  return [...passiveRows]
    .sort((a, b) => passiveRowRecencyMs(b) - passiveRowRecencyMs(a))
    .slice(0, limit);
}

/**
 * Orders passive rows with recent sessions first, then the rest without duplicates.
 */
export function orderPassiveRowsRecentFirst(
  passiveRows: ProjectHomePassiveRow[],
  recentLimit = 4,
): ProjectHomePassiveRow[] {
  const recent = pickRecentPassiveRows(passiveRows, recentLimit);
  const recentIds = new Set(recent.map((row) => row.conversation.id));
  const rest = passiveRows.filter((row) => !recentIds.has(row.conversation.id));
  return [...recent, ...rest];
}

export function filterProjectHomeConversationRows(
  query: string,
  decisions: CNSProjectNextStep[],
  passiveRows: ProjectHomePassiveRow[],
  conversationsById: Map<string, CNSConversation>,
): { decisions: CNSProjectNextStep[]; passiveRows: ProjectHomePassiveRow[] } {
  // Search applies to the first loaded page of conversations (~20) until pagination follow-up.
  const normalizedQuery = normalizeConversationSearchQuery(query);
  if (!normalizedQuery) {
    return { decisions, passiveRows };
  }
  return {
    decisions: filterDecisionSteps(decisions, query, conversationsById),
    passiveRows: filterPassiveRows(passiveRows, query),
  };
}

export type ProjectHomeWaitingSummaryLocation = "intent-below" | "paths-header";

/**
 * Formats waiting copy from distinct conversation count and open decision row count.
 * Returns null when there are no open decisions.
 */
export function formatProjectHomeWaitingSummary(
  waitingConversationCount: number,
  openDecisionCount: number,
  location: ProjectHomeWaitingSummaryLocation = "intent-below",
): string | null {
  if (openDecisionCount <= 0) {
    return null;
  }

  const header = location === "paths-header";
  const judgment = header ? " waiting on your judgment" : " waiting below";
  const needsJudgment = header ? " waiting on your judgment" : " need your judgment below";

  if (waitingConversationCount <= 0) {
    const decisions =
      openDecisionCount === 1 ? "1 decision" : `${openDecisionCount} decisions`;
    return `${decisions}${judgment}`;
  }

  if (openDecisionCount > waitingConversationCount) {
    const conversations =
      waitingConversationCount === 1
        ? "1 conversation"
        : `${waitingConversationCount} conversations`;
    const decisions =
      openDecisionCount === 1 ? "1 decision" : `${openDecisionCount} decisions`;
    return `${conversations} · ${decisions}${judgment}`;
  }

  if (waitingConversationCount === 1) {
    return header
      ? "1 conversation waiting on your judgment"
      : "1 conversation needs your judgment below";
  }

  return header
    ? `${waitingConversationCount} conversations waiting on your judgment`
    : `${waitingConversationCount} conversations need your judgment below`;
}

/**
 * Formats project-home liveness copy from tab-local running conversations and open decisions.
 * Returns null when both counts are zero.
 */
export function formatProjectHomeLivenessLine(
  runningCount: number,
  waitingCount: number,
): string | null {
  if (runningCount <= 0 && waitingCount <= 0) {
    return null;
  }

  const parts: string[] = [];
  if (runningCount > 0) {
    parts.push(
      runningCount === 1 ? "1 conversation running" : `${runningCount} conversations running`,
    );
  }
  if (waitingCount > 0) {
    parts.push(
      waitingCount === 1
        ? "1 waiting on you"
        : `${waitingCount} waiting on you`,
    );
  }
  return parts.join(" · ");
}
